import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { billingAccessCopy, billingDisputeCopy, billingEntitlements, normalizeBillingResponse, rememberBillingSelection } from '../billing.js';
import { ConfirmationDialog, useConfirmationDialog } from './ConfirmationDialog.jsx';
import { Button } from './primitives.jsx';
import Beacon from './Beacon.jsx';
import './company-billing.css';
import NewCampaignSubscriptionModal from './NewCampaignSubscriptionModal.jsx';
import { newCampaignSelection } from '../billing-navigation.js';

// Isolated screen tests and the preserved legacy shell have no billing
// provider. Real tenant routes always mount CompanyBillingProvider below.
const DEFAULT_BILLING = Object.freeze({ status: 'disabled', billing: null, enabled: false, canCreate: true, canResume: true, refresh: async () => null, requestAccess: async () => true, showCreationPrompt: () => false });
export const CompanyBillingContext = createContext(DEFAULT_BILLING);
const Context = CompanyBillingContext;
export function useCompanyBilling() { return useContext(Context); }

export function CompanyBillingProvider({ session, navigate, children }) {
  const companyId = session?.user?.activeCompany?.id || session?.user?.tenant?.companyId || '';
  const role = session?.user?.role || '';
  const enabled = session?.user?.auth?.type === 'supabase' && Boolean(companyId);
  // Keying the state owner discards every old-company request, dialog and
  // mutation when the active tenant changes, including switch-away-and-back.
  return <BillingState key={`${enabled ? companyId : 'disabled'}:${role}`} companyId={companyId} role={role} enabled={enabled} navigate={navigate}>{children}</BillingState>;
}

function BillingState({ companyId, role, enabled, navigate, children }) {
  const [state, setState] = useState({ companyId, status: enabled ? 'loading' : 'disabled', snapshot: null });
  const [clock, setClock] = useState(Date.now());
  const alive = useRef(true);
  const version = useRef(0);
  const latest = useRef(state);
  latest.current = state;
  const confirmation = useConfirmationDialog();
  const [creationPrompt, setCreationPrompt] = useState(null);
  const showCreationPrompt = useCallback((selectedTypes = []) => {
    const current = latest.current;
    if (!enabled || current.status !== 'ready' || current.companyId !== companyId || current.snapshot?.billing?.enabled !== true || billingEntitlements(current, companyId).canCreate) return false;
    if (selectedTypes.length) rememberBillingSelection(companyId, selectedTypes);
    const invoker = typeof document !== 'undefined' ? document.activeElement : null;
    setCreationPrompt({ returnFocus: invoker });
    return true;
  }, [companyId, enabled]);
  const navigateWithBilling = useCallback((path, options) => {
    const selected = newCampaignSelection(path);
    if (selected !== null && showCreationPrompt(selected)) return;
    navigate?.(path, options);
  }, [navigate, showCreationPrompt]);
  const refresh = useCallback(async () => {
    if (!enabled) return null;
    const request = ++version.current;
    try {
      const snapshot = normalizeBillingResponse(await api.get('/api/saas/billing', { headers: { 'x-company-id': companyId }, redirectOnUnauthorized: false }), companyId, role);
      if (!alive.current || request !== version.current) return null;
      const next = { companyId, status: 'ready', snapshot };
      latest.current = next;
      setState(next);
      setClock(Date.now());
      return snapshot;
    } catch (error) {
      if (!alive.current || request !== version.current) return null;
      const next = { companyId, status: 'error', snapshot: null, error };
      latest.current = next;
      setState(next);
      return null;
    }
  }, [companyId, role, enabled]);

  useEffect(() => {
    alive.current = true;
    refresh();
    const onFocus = () => { if (document.visibilityState !== 'hidden') refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const interval = enabled ? setInterval(onFocus, 60_000) : null;
    return () => { alive.current = false; version.current += 1; clearInterval(interval); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [refresh, enabled]);

  const billing = state.snapshot?.billing || null;
  useEffect(() => {
    const deadlines = [billing?.accessExpiresAt].map(Date.parse).filter((date) => Number.isFinite(date) && date > Date.now());
    if (!deadlines.length) return undefined;
    const timer = setTimeout(() => { setClock(Date.now()); refresh(); }, Math.min(Math.min(...deadlines) - Date.now() + 10, 2_147_483_647));
    return () => clearTimeout(timer);
  }, [billing, refresh, clock]);

  const access = enabled ? billingEntitlements(state, companyId, clock) : { canCreate: true, canResume: true };
  useEffect(() => {
    if (state.status !== 'ready' || access.canCreate) setCreationPrompt(null);
  }, [state.status, access.canCreate]);
  const requestAccess = useCallback(async (action = 'create') => {
    if (!enabled) return true;
    const currentAccess = billingEntitlements(latest.current, companyId);
    if (action === 'create' && !currentAccess.canCreate && showCreationPrompt()) return false;
    if (action === 'resume' ? currentAccess.canResume : currentAccess.canCreate) return true;
    const snapshot = await refresh();
    if (!alive.current) return false;
    const nextAccess = billingEntitlements(latest.current, companyId);
    if (action === 'resume' ? nextAccess.canResume : nextAccess.canCreate) return true;
    if (action === 'create') {
      // A failed or still-loading read is not evidence that this company must
      // buy. The existing status notice provides retry without an upsell.
      showCreationPrompt();
      return false;
    }
    const value = snapshot?.billing;
    const manage = value?.canManage;
    const accepted = await confirmation.request({
      eyebrow: 'Company subscription', title: billingDisputeCopy(value)?.title || (action === 'resume' ? 'Paid access is required to resume ads' : 'Subscribe to create campaigns'),
      description: billingAccessCopy(value, { resume: action === 'resume' }),
      details: value?.canCheckout && !billingDisputeCopy(value) ? [{ label: 'Monthly subscription', value: '$499 USD/month + applicable tax' }, { label: 'Annual subscription', value: '$4,990 USD/year + applicable tax' }, { label: 'Included', value: 'Unlimited campaigns and no campaign duration limit while access is active. Meta ad spend is separate.' }] : [],
      confirmLabel: manage ? 'View billing' : 'View company team', cancelLabel: 'Keep browsing', tone: 'accent',
    });
    if (accepted && alive.current) navigate?.(manage ? 'billing' : 'company');
    return false;
  }, [enabled, refresh, companyId, confirmation.request, navigate, showCreationPrompt]);

  const value = useMemo(() => ({ ...state, ...access, enabled: enabled && billing?.enabled !== false, billing, plans: state.snapshot?.plans || [], termsVersion: state.snapshot?.termsVersion || null, refresh, requestAccess, showCreationPrompt }), [state, access.canCreate, access.canResume, enabled, billing, refresh, requestAccess, showCreationPrompt]);
  return <Context.Provider value={value}>{typeof children === 'function' ? children(navigateWithBilling) : children}<ConfirmationDialog {...confirmation.dialogProps} /><NewCampaignSubscriptionModal open={Boolean(creationPrompt && state.status === 'ready' && !access.canCreate)} billing={billing} returnFocus={creationPrompt?.returnFocus || null} onClose={() => setCreationPrompt(null)} onViewBilling={() => { setCreationPrompt(null); navigate?.('billing'); }} /></Context.Provider>;
}

export function BillingNotice({ navigate, contextualOnly = false, action = 'create' }) {
  const state = useCompanyBilling();
  const billing = state.billing;
  if (!state.enabled || state.status === 'disabled') return null;
  if (state.status === 'loading') return <div className="bl-billing-notice" role="status"><Beacon size="inline" delayMs={0} slowAfterMs={null} /><span>Checking company subscription…</span></div>;
  if (state.status === 'error') return <div className="bl-billing-notice is-warning" role="status"><div><strong>Subscription status is unavailable</strong><p>Campaign creation and resumption stay blocked until access can be verified. You can continue browsing.</p></div><Button size="sm" variant="ghost" onClick={state.refresh}>Check again</Button></div>;
  if (!billing) return null;
  let title = '';
  let text = '';
  if (billing.pauseStatus === 'pending' || billing.pauseStatus === 'failed') {
    title = billing.pauseStatus === 'failed' ? 'Meta has not confirmed campaign pausing' : 'Campaign pausing is pending';
    text = 'BeyondLeads requested pausing of its managed campaigns. Ads may continue spending until Meta confirms. Check the campaigns in Meta Ads Manager; ad spend remains your responsibility.';
  } else if (billing.status === 'disputed') {
    title = billingDisputeCopy(billing).title; text = billingAccessCopy(billing);
  } else if (billing.status === 'refunded') {
    title = 'Current paid period refunded'; text = billingAccessCopy(billing);
  } else if (billing.graceEndsAt && state.canCreate && !billing.exemptionActive) {
    title = 'Renewal payment failed'; text = `Creation grace ends ${billingDate(billing.graceEndsAt, true)}. Update payment details before this exact 14-day deadline. Confirmed payment is required to resume ads.`;
  } else if (!state.canCreate || (action === 'resume' && !state.canResume)) {
    if (contextualOnly && billing.status === 'unpaid') return null;
    title = action === 'resume' ? 'Paid access required to resume ads' : 'Campaign creation is blocked';
    text = billingAccessCopy(billing, { resume: action === 'resume' });
  } else if (billing.cancelAtPeriodEnd && billing.status !== 'exempt') {
    title = 'Renewal is canceled'; text = billing.exemptionActive ? 'Your subscription renewal is canceled. Your payment exemption continues according to its own expiration.' : `Paid access continues through ${billingDate(billing.currentPeriodEnd)}. At expiry, creation stops and BeyondLeads requests pausing of its managed campaigns.`;
  }
  if (!title) return null;
  return <div className="bl-billing-notice is-warning" role="status"><div><strong>{title}</strong><p>{text}</p>{billing.pauseStatus === 'confirmed' && !state.canCreate ? <p>Managed ads are paused. Restoring paid access never resumes ads automatically.</p> : null}</div>{navigate ? <Button size="sm" variant="ghost" onClick={() => navigate('billing')}>View billing</Button> : null}</div>;
}

export function BillingCreationGate({ children, fallback = null, selectedTypes = [], navigate, required = true }) {
  const state = useCompanyBilling();
  // Access can expire while a person is editing. Keep an already-open form
  // mounted so unsaved work is not silently discarded; every create action
  // remains gated in its component and again on the server.
  const openedWithAccess = useRef(state.canCreate);
  const prompted = useRef(false);
  useEffect(() => {
    if (required && !openedWithAccess.current && !prompted.current && state.showCreationPrompt(selectedTypes)) {
      prompted.current = true;
      navigate?.('home', { replace: true });
    }
  }, [required, selectedTypes, navigate, state.showCreationPrompt, state.status, state.canCreate]);
  // Keep this wrapper mounted when a new draft adopts its first durable id.
  // Removing it would remount setup and discard edits, request guards/focus.
  if (!required) {
    // A saved draft is always browseable, but it cannot lend permission to a
    // later explicit new-draft route after the subscription has expired.
    openedWithAccess.current = false;
    return children;
  }
  if (state.canCreate) openedWithAccess.current = true;
  if (openedWithAccess.current) return children;
  // AppShell already shows the subscription-check status. Wait for that read
  // before mounting Home, whose catalog and media are unrelated to this route.
  if (state.status === 'loading') return null;
  return fallback;
}

export function billingDate(value, exact = false) {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', ...(exact ? { timeStyle: 'short', timeZoneName: undefined } : {}) }).format(date) : 'Not available';
}
